import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface NavigationProps {
  isAuthenticated?: boolean;
}

const Navigation: React.FC<NavigationProps> = ({ isAuthenticated = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white shadow-sm border-b border-rowing-gray-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to={isAuthenticated ? "/dashboard" : "/"}>
              <h1 className="text-2xl font-display font-bold text-rowing-navy">
                GLYYD
              </h1>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            {!isAuthenticated ? (
              <>
                <a href="#features" className="nav-link">
                  Features
                </a>
                <a href="#how-it-works" className="nav-link">
                  How It Works
                </a>
                <a href="/about" className="nav-link">
                  About Us
                </a>
                <a href="/pricing" className="nav-link">
                  Pricing
                </a>
              </>
            ) : (
              <>
                <a href="/dashboard" className="nav-link">
                  Dashboard
                </a>
                <a href="/schools" className="nav-link">
                  Schools
                </a>
                <a href="/about" className="nav-link">
                  About Us
                </a>
                <a href="/pricing" className="nav-link">
                  Pricing
                </a>
              </>
            )}
          </div>

          {/* Auth Section */}
          <div className="flex items-center space-x-4">
            {!isAuthenticated ? (
              <>
                <Link to="/login" className="text-rowing-gray-700 hover:text-rowing-blue font-medium">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary">
                  Get Started
                </Link>
              </>
            ) : (
              <div className="relative">
                <button 
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center space-x-2 text-rowing-gray-700 hover:text-rowing-blue"
                >
                  <div className="w-10 h-10 bg-rowing-gray-200 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </button>
                
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-rowing-gray-200 py-1">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-rowing-gray-700 hover:bg-rowing-gray-50"
                      onClick={() => setShowDropdown(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-rowing-gray-700 hover:bg-rowing-gray-50"
                      onClick={() => setShowDropdown(false)}
                    >
                      Settings
                    </Link>
                    <hr className="my-1 border-rowing-gray-200" />
                    <button
                      onClick={async () => {
                        await signOut();
                        navigate('/');
                        setShowDropdown(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-rowing-gray-700 hover:bg-rowing-gray-50"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button className="text-rowing-gray-700 hover:text-rowing-blue">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navigation;